import { Card, CardContent } from './card'
import { Skeleton } from './skeleton'
import { cn } from '@/lib/utils'

interface TableSkeletonProps {
  className?: string
  variant?: 'default' | 'elevated' | 'outlined' | 'glass'
  rows?: number
  columns?: number
  showHeader?: boolean
  showFilters?: boolean
}

const TableSkeleton = ({
  className,
  variant = 'elevated',
  rows = 5,
  columns = 6,
  showHeader = true,
  showFilters = true
}: TableSkeletonProps) => {
  return (
    <Card variant={variant} className={cn("", className)}>
      <CardContent className="p-6">
        {/* Header */}
        {showHeader && (
          <div className="mb-6">
            <Skeleton className="h-8 w-1/3 mb-2" />
            <Skeleton className="h-4 w-1/2" />
          </div>
        )}
        
        {/* Filters */}
        {showFilters && (
          <div className="mb-6 space-y-4">
            <div className="flex gap-4 flex-wrap">
              <Skeleton className="h-10 w-48" />
              <Skeleton className="h-10 w-32" />
              <Skeleton className="h-10 w-32" />
            </div>
          </div>
        )}
        
        {/* Table */}
        <div className="space-y-3">
          {/* Table headers */}
          <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}>
            {Array.from({ length: columns }).map((_, index) => (
              <Skeleton key={index} className="h-6 w-full" />
            ))}
          </div>
          
          {/* Table rows */}
          {Array.from({ length: rows }).map((_, rowIndex) => (
            <div 
              key={rowIndex} 
              className="grid gap-4 py-3 border-b border-border last:border-b-0"
              style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}
            >
              {Array.from({ length: columns }).map((_, colIndex) => (
                <div key={colIndex} className="flex items-center gap-2">
                  {colIndex === 0 && (
                    <Skeleton className="h-6 w-6 rounded-full" />
                  )}
                  <Skeleton 
                    className={cn(
                      "h-4",
                      colIndex === 0 ? "w-3/4" : 
                      colIndex === columns - 1 ? "w-1/2" : "w-full"
                    )} 
                  />
                </div>
              ))}
            </div>
          ))}
        </div>
        
        {/* Pagination */}
        <div className="mt-6 flex justify-center">
          <div className="flex gap-2">
            <Skeleton className="h-8 w-8" />
            <Skeleton className="h-8 w-8" />
            <Skeleton className="h-8 w-8" />
            <Skeleton className="h-8 w-8" />
            <Skeleton className="h-8 w-8" />
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

export { TableSkeleton } 